if (!customElements.get('wlsn-ugc')) {
  class WLSNUGC extends HTMLElement {
    constructor() {
      super();

      // CSS classes and attributes
      this.classes = {
        video: 'wlsn-video',
        slide: 'wlsn-slider__slide',
        slideActive: 'swiper-slide-active',
        slideHover: 'wlsn-slider__slide--hover',
        playing: 'playing',
        swiper: 'swiper',
      };

      this.attrs = {
        slideHoverInitialized: 'data-slide-hover-initialized',
        hoverInitialized: 'data-hover-initialized',
        autoplay: 'autoplay',
        allowAutoplayOnHover: 'data-allow-autoplay-on-hover',
      };

      this.selectors = {
        video: 'wlsn-video',
        slider: 'wlsn-slider',
        slide: `.${this.classes.slide}`,
        swiper: `.${this.classes.swiper}`,
      };

      // State tracking for hover autoplay per video
      this.videoStates = new WeakMap();

      // Desktop media query for hover functionality
      this.desktopMediaQuery = window.matchMedia('(min-width: 1024px)');

      // Bound handlers
      this.boundHandleVideoEnded = this._handleVideoEnded.bind(this);
      this.boundHandleResize = this._handleResize.bind(this);
      this.boundHandleSlideHover = this._handleSlideHover.bind(this);

      // Mutation observer to watch for dynamically added videos
      this.videoObserver = new MutationObserver(this._handleVideoMutation.bind(this));
      
      // Mutation observer to watch for slide class changes (active/inactive)
      this.slideObserver = new MutationObserver(this._handleSlideMutation.bind(this));
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        videos: this.querySelectorAll(this.selectors.video),
        slides: this.querySelectorAll(this.selectors.slide),
        slider: this.querySelector(this.selectors.slider),
        swiper: this.querySelector(this.selectors.swiper),
      };
    }

    /**
     * Check if current screen is desktop
     * @returns {boolean} - True if screen width is desktop (≥1024px)
     */
    _isDesktop() {
      return this.desktopMediaQuery.matches;
    }


    /**
     * Initialize slide hover functionality
     * @returns {void}
     */
    _initSlideHover() {
      if (!this._isDesktop()) return;

      const slides = this.elements.slides;
      slides.forEach((slide) => {
        // Skip active slide - don't add hover to active slide
        if (slide.classList.contains(this.classes.slideActive)) {
          // Remove hover if it was previously added
          if (slide.hasAttribute(this.attrs.slideHoverInitialized)) {
            slide.removeEventListener('mouseenter', this.boundHandleSlideHover);
            slide.removeEventListener('mouseleave', this.boundHandleSlideHover);
            slide.removeAttribute(this.attrs.slideHoverInitialized);
            slide.classList.remove(this.classes.slideHover);
          }
          return;
        }

        // Skip if already initialized
        if (slide.hasAttribute(this.attrs.slideHoverInitialized)) return;

        slide.addEventListener('mouseenter', this.boundHandleSlideHover);
        slide.addEventListener('mouseleave', this.boundHandleSlideHover);
        slide.setAttribute(this.attrs.slideHoverInitialized, '');
      });
    }

    /**
     * Handle slide mutation (class changes for active/inactive)
     * @param {Array<MutationRecord>} mutations - Mutation records
     * @returns {void}
     */
    _handleSlideMutation(mutations) {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'class') {
          const slide = mutation.target;
          // Re-initialize hover when slide becomes active/inactive
          if (slide.classList.contains(this.classes.slide) && this._isDesktop()) {
            // Remove hover from newly active slide
            if (slide.classList.contains(this.classes.slideActive)) {
              if (slide.hasAttribute(this.attrs.slideHoverInitialized)) {
                slide.removeEventListener('mouseenter', this.boundHandleSlideHover);
                slide.removeEventListener('mouseleave', this.boundHandleSlideHover);
                slide.removeAttribute(this.attrs.slideHoverInitialized);
                slide.classList.remove(this.classes.slideHover);
              }
            } else {
              // Add hover to newly inactive slide
              if (!slide.hasAttribute(this.attrs.slideHoverInitialized)) {
                slide.addEventListener('mouseenter', this.boundHandleSlideHover);
                slide.addEventListener('mouseleave', this.boundHandleSlideHover);
                slide.setAttribute(this.attrs.slideHoverInitialized, '');
              }
            }
          }
        }
      });
    }

    /**
     * Remove slide hover functionality
     * @returns {void}
     */
    _removeSlideHover() {
      const slides = this.elements.slides;
      slides.forEach((slide) => {
        slide.removeEventListener('mouseenter', this.boundHandleSlideHover);
        slide.removeEventListener('mouseleave', this.boundHandleSlideHover);
        slide.removeAttribute(this.attrs.slideHoverInitialized);
        slide.classList.remove(this.classes.slideHover);
      });
    }

    /**
     * Handle slide hover event (both enter and leave)
     * @param {Event} event - Mouse event
     * @returns {void}
     */
    _handleSlideHover(event) {
      if (!this._isDesktop()) return;

      const slide = event.currentTarget;
      if (!slide) return;

      // Skip active slide
      if (slide.classList.contains(this.classes.slideActive)) return;

      if (event.type === 'mouseenter') {
        // Add hover class to restore default state
        slide.classList.add(this.classes.slideHover);

        // Find and play video if exists
        const videoElement = slide.querySelector(this.selectors.video);
        if (videoElement) {
          const video = videoElement.getVisibleVideo ? videoElement.getVisibleVideo() : null;
          if (video && video.paused) {
            // Get state for this video
            const state = this.videoStates.get(videoElement);
            // Don't interfere if video was manually played
            if (!state || !state.isManuallyPlayed) {
              if (videoElement.videoPlay) {
                videoElement.videoPlay();
              } else {
                video.play().catch((error) => {
                  console.warn('Failed to play video on hover:', error);
                });
                videoElement.classList.add(this.classes.playing);
              }
              if (state) {
                state.isHoverPlayed = true;
              }
            }
          }
        }
      } else if (event.type === 'mouseleave') {
        // Remove hover class
        slide.classList.remove(this.classes.slideHover);

        // Find and pause video if it was hover-played
        const videoElement = slide.querySelector(this.selectors.video);
        if (videoElement) {
          const video = videoElement.getVisibleVideo ? videoElement.getVisibleVideo() : null;
          if (video && !video.paused) {
            const state = this.videoStates.get(videoElement);
            // Only pause if it was hover-played (not manually played)
            if (state && state.isHoverPlayed && !state.isManuallyPlayed) {
              if (!video.hasAttribute(this.attrs.autoplay)) {
                video.pause();
                videoElement.classList.remove(this.classes.playing);
                state.isHoverPlayed = false;
              }
            }
          }
        }
      }
    }

    /**
     * Initialize hover functionality for a video element
     * @param {HTMLElement} videoElement - The wlsn-video element
     * @returns {void}
     */
    _initVideoHover(videoElement) {
      // Skip if already initialized
      if (this.videoStates.has(videoElement)) return;

      // Initialize state for this video
      this.videoStates.set(videoElement, {
        isManuallyPlayed: false,
        isHoverPlayed: false,
        boundHandleMouseEnter: this._handleMouseEnter.bind(this, videoElement),
        boundHandleMouseLeave: this._handleMouseLeave.bind(this, videoElement),
        boundHandlePlay: this._handleVideoPlay.bind(this, videoElement),
        boundHandlePause: this._handleVideoPause.bind(this, videoElement),
        boundHandleEnded: this._handleVideoEnded.bind(this, videoElement),
        isHovering: false,
      });

      const state = this.videoStates.get(videoElement);
      const video = videoElement.getVisibleVideo ? videoElement.getVisibleVideo() : null;

      if (!video) {
        // Wait for video to be available (in case it's not loaded yet)
        const checkVideo = setInterval(() => {
          const v = videoElement.getVisibleVideo ? videoElement.getVisibleVideo() : null;
          if (v) {
            clearInterval(checkVideo);
            this._attachVideoListeners(videoElement, v, state);
          }
        }, 100);
        // Stop checking after 5 seconds
        setTimeout(() => clearInterval(checkVideo), 5000);
        return;
      }

      this._attachVideoListeners(videoElement, video, state);
    }

    /**
     * Attach event listeners to video element
     * @param {HTMLElement} videoElement - The wlsn-video element
     * @param {HTMLVideoElement} video - The video element
     * @param {Object} state - The state object for this video
     * @returns {void}
     */
    _attachVideoListeners(videoElement, video, state) {
      // Add play/pause handlers to track manual play
      video.addEventListener('play', state.boundHandlePlay);
      video.addEventListener('pause', state.boundHandlePause);
      video.addEventListener('ended', state.boundHandleEnded);

      // Add hover listeners only on desktop
      if (this._isDesktop()) {
        videoElement.addEventListener('mouseenter', state.boundHandleMouseEnter);
        videoElement.addEventListener('mouseleave', state.boundHandleMouseLeave);
      }
    }

    /**
     * Remove hover functionality from a video element
     * @param {HTMLElement} videoElement - The wlsn-video element
     * @returns {void}
     */
    _removeVideoHover(videoElement) {
      const state = this.videoStates.get(videoElement);
      if (!state) return;

      const video = videoElement.getVisibleVideo ? videoElement.getVisibleVideo() : null;

      if (video) {
        video.removeEventListener('play', state.boundHandlePlay);
        video.removeEventListener('pause', state.boundHandlePause);
        video.removeEventListener('ended', state.boundHandleEnded);
      }

      videoElement.removeEventListener('mouseenter', state.boundHandleMouseEnter);
      videoElement.removeEventListener('mouseleave', state.boundHandleMouseLeave);

      this.videoStates.delete(videoElement);
    }

    /**
     * Handle mouse enter event for hover autoplay
     * @param {HTMLElement} videoElement - The wlsn-video element
     * @returns {void}
     */
    _handleMouseEnter(videoElement) {
      // Only work on desktop
      if (!this._isDesktop()) return;

      const state = this.videoStates.get(videoElement);
      if (!state) return;

      state.isHovering = true;

      const video = videoElement.getVisibleVideo ? videoElement.getVisibleVideo() : null;
      if (!video) return;

      // Don't do anything if video is already playing
      if (!video.paused) return;

      // Don't interfere if video was manually played
      if (state.isManuallyPlayed) return;

      // Play video on hover
      // Use videoPlay method if available, otherwise play directly
      if (videoElement.videoPlay) {
        videoElement.videoPlay();
      } else {
        video.play();
        videoElement.classList.add(this.classes.playing);
      }
      state.isHoverPlayed = true;
    }

    /**
     * Handle mouse leave event for hover pause
     * @param {HTMLElement} videoElement - The wlsn-video element
     * @returns {void}
     */
    _handleMouseLeave(videoElement) {
      // Only work on desktop
      if (!this._isDesktop()) return;

      const state = this.videoStates.get(videoElement);
      if (!state) return;

      state.isHovering = false;

      const video = videoElement.getVisibleVideo ? videoElement.getVisibleVideo() : null;
      if (!video) return;

      // Only pause if video was hover-played (not manually played)
      if (state.isHoverPlayed && !state.isManuallyPlayed) {
        if (!video.hasAttribute(this.attrs.autoplay)) {
          video.pause();
          videoElement.classList.remove(this.classes.playing);
        }
        state.isHoverPlayed = false;
      }
    }

    /**
     * Handle video play event to track manual play
     * @param {HTMLElement} videoElement - The wlsn-video element
     * @returns {void}
     */
    _handleVideoPlay(videoElement) {
      const state = this.videoStates.get(videoElement);
      if (!state) return;

      // If video started playing and we're not hovering, it's a manual play
      if (!state.isHovering && !state.isHoverPlayed) {
        state.isManuallyPlayed = true;
        state.isHoverPlayed = false;
      }
    }

    /**
     * Handle video pause event
     * @param {HTMLElement} videoElement - The wlsn-video element
     * @returns {void}
     */
    _handleVideoPause(videoElement) {
      const state = this.videoStates.get(videoElement);
      if (!state) return;

      // If we're not hovering and video paused, reset manual play state
      if (!state.isHovering) {
        state.isManuallyPlayed = false;
        state.isHoverPlayed = false;
      }
    }


    /**
     * Handle video ended event, reset state
     * @param {HTMLElement} videoElement - The wlsn-video element
     * @returns {void}
     */
    _handleVideoEnded(videoElement) {
      const state = this.videoStates.get(videoElement);
      if (state) {
        state.isManuallyPlayed = false;
        state.isHoverPlayed = false;
      }
    }

    /**
     * Handle resize event to add/remove hover listeners
     * @returns {void}
     */
    _handleResize() {
      const isDesktop = this._isDesktop();
      const videos = this.elements.videos;

      videos.forEach((videoElement) => {
        const state = this.videoStates.get(videoElement);
        if (!state) return;

        if (isDesktop) {
          // Add hover listeners if desktop
          if (!videoElement.hasAttribute(this.attrs.hoverInitialized)) {
            videoElement.addEventListener('mouseenter', state.boundHandleMouseEnter);
            videoElement.addEventListener('mouseleave', state.boundHandleMouseLeave);
            videoElement.setAttribute(this.attrs.hoverInitialized, '');
          }
        } else {
          // Remove hover listeners if not desktop
          videoElement.removeEventListener('mouseenter', state.boundHandleMouseEnter);
          videoElement.removeEventListener('mouseleave', state.boundHandleMouseLeave);
          videoElement.removeAttribute(this.attrs.hoverInitialized);
        }
      });

      // Handle slide hover based on desktop/mobile
      if (isDesktop) {
        this._initSlideHover();
      } else {
        this._removeSlideHover();
      }
    }

    /**
     * Handle mutation observer callback for dynamically added videos
     * @param {Array<MutationRecord>} mutations - Mutation records
     * @returns {void}
     */
    _handleVideoMutation(mutations) {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            // Check if the added node is a wlsn-video
            if (node.tagName === 'WLSN-VIDEO') {
              this._initVideoHover(node);
            }
            // Check for wlsn-video elements within the added node
            const videos = node.querySelectorAll ? node.querySelectorAll(this.selectors.video) : [];
            videos.forEach((videoElement) => {
              this._initVideoHover(videoElement);
            });
          }
        });
      });
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      if (!this.hasAttribute(this.attrs.allowAutoplayOnHover)) {
        return;
      }

      // Wait for custom elements to be defined and DOM to be ready
      const initVideos = () => {
        // Initialize existing videos
        const videos = this.elements.videos;
        videos.forEach((videoElement) => {
          this._initVideoHover(videoElement);
        });
      };

      // If custom elements are already defined, initialize immediately
      if (customElements.get(this.selectors.video)) {
        // Use setTimeout to ensure wlsn-video elements are fully initialized
        setTimeout(initVideos, 0);
      } else {
        // Wait for custom element to be defined
        customElements.whenDefined(this.selectors.video).then(() => {
          setTimeout(initVideos, 0);
        });
      }

      // Watch for dynamically added videos
      this.videoObserver.observe(this, {
        childList: true,
        subtree: true,
      });

      // Watch for slide class changes (active/inactive)
      const slider = this.elements.slider;
      if (slider) {
        const swiperElement = this.elements.swiper;
        if (swiperElement) {
          this.slideObserver.observe(swiperElement, {
            attributes: true,
            attributeFilter: ['class'],
            subtree: true,
            childList: false,
          });
        }
      }

      // Listen for resize events to handle desktop/mobile switching
      this.desktopMediaQuery.addEventListener('change', this.boundHandleResize);

      // Initialize slide hover functionality
      setTimeout(() => {
        this._initSlideHover();
      }, 300);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      // Remove hover functionality from all videos
      const videos = this.elements.videos;
      videos.forEach((videoElement) => {
        this._removeVideoHover(videoElement);
      });

      // Remove slide hover functionality
      this._removeSlideHover();

      // Disconnect observers
      this.videoObserver.disconnect();
      if (this.slideObserver) {
        this.slideObserver.disconnect();
      }
      this.desktopMediaQuery.removeEventListener('change', this.boundHandleResize);
    }
  }

  customElements.define('wlsn-ugc', WLSNUGC);
}

